import { useCallback, useEffect, useMemo, useState } from 'react';
import { RcFile } from 'antd/lib/upload';
import Notification from 'components/ui/base/notification/Notification';
import useSession from 'hooks/useSession';
import usePollPendingSecurityChecks from 'hooks/usePollPendingSecurityChecks';
import { confirmUploadFile, documentsPreUpload, downloadFile } from 'store/documents/client';
import { createDocumentIcs2, deleteDocument, listDocumentsIcs2 } from 'store/file-upload/client';
import {
    ICS2_ATTACHMENT_FILE_TYPES,
    ICS2_MAX_ATTACHMENTS_PER_REFERRAL,
    Ics2UploadedFile,
    Ics2UploadedFileStatus,
    getIcs2ReferralDocumentPurpose,
    isIcs2DocumentSendable,
    isSelectableIcs2Document,
} from 'store/file-upload/ics2';
import { triggerBrowserDownload } from 'utils/download-utils';
import { getErrorMessage } from 'utils/getErrorMessage';
import { uploadFile } from 'utils/uploadFile';

type Args = {
    declarationId?: string;
    notificationId?: string;
    referralRequestReference?: string;
    isScreening: boolean;
};

const acceptedExtensions = ICS2_ATTACHMENT_FILE_TYPES.map((type) => type.toLowerCase());

const useIcs2ReferralAttachments = ({ declarationId, notificationId, referralRequestReference, isScreening }: Args) => {
    const { customerId } = useSession();
    const [documents, setDocuments] = useState<Ics2UploadedFile[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);

    const purpose = getIcs2ReferralDocumentPurpose(isScreening);

    const load = useCallback(
        async (silent = false) => {
            if (!declarationId || !notificationId || !referralRequestReference) {
                setIsLoading(false);
                return;
            }
            if (!silent) setIsLoading(true);
            try {
                const files = await listDocumentsIcs2(declarationId, {
                    process: purpose,
                    referralRequestReference,
                    requestNotificationId: notificationId,
                });
                // Sent evidence moves to SUBMITTED/SUCCESS, so filtering to the selectable ones here
                // would empty the list on exactly the answers where it is the only record of what went.
                setDocuments(files.filter((file) => file.status !== Ics2UploadedFileStatus.DELETING));
                setLastFetchedAt(Date.now());
            } catch (error) {
                if (silent) return;
                Notification({
                    type: 'error',
                    messageTitle: 'Could not load attachments',
                    description: getErrorMessage(error) ?? 'Please try again.',
                    placement: 'top',
                });
            } finally {
                if (!silent) setIsLoading(false);
            }
        },
        [declarationId, notificationId, referralRequestReference, purpose]
    );

    useEffect(() => {
        load();
    }, [load]);

    const refreshSilently = useCallback(() => {
        load(true);
    }, [load]);

    // The scan finishes server-side with nothing to tell us about it, so the row only flips out of
    // "Checking…" if we go back and look.
    usePollPendingSecurityChecks(documents, refreshSilently, lastFetchedAt, {
        enabled: Boolean(declarationId && notificationId && referralRequestReference),
    });

    const selectable = useMemo(() => documents.filter(isSelectableIcs2Document), [documents]);

    const upload = useCallback(
        async (file: RcFile) => {
            if (!declarationId || !customerId || !notificationId || !referralRequestReference) return;

            const extension = file.name.split('.').at(-1)?.toLowerCase();
            if (!extension || !acceptedExtensions.includes(extension)) {
                Notification({
                    type: 'error',
                    messageTitle: 'File upload failed: file type not supported.',
                    description: `Customs accepts only ${ICS2_ATTACHMENT_FILE_TYPES.join(
                        ', '
                    )} files on a referral response.`,
                    placement: 'top',
                });
                return;
            }

            if (selectable.length >= ICS2_MAX_ATTACHMENTS_PER_REFERRAL) {
                Notification({
                    type: 'error',
                    messageTitle: 'Too many attachments',
                    description: `A response may carry at most ${ICS2_MAX_ATTACHMENTS_PER_REFERRAL} files per referral.`,
                    placement: 'top',
                });
                return;
            }

            setIsUploading(true);
            try {
                const { preSignedUrl } = await documentsPreUpload({
                    fileName: file.name,
                    fileSize: file.size,
                    customerId,
                    declarationId,
                });
                await uploadFile(file as unknown as File, preSignedUrl);

                const s3id = preSignedUrl.match(/\/([^/?]+)\?/)?.[1];
                if (!s3id) throw new Error('Missing file identifier');

                const { payload: cmsFile } = await confirmUploadFile({
                    s3id,
                    fileName: file.name,
                    fileSize: file.size,
                    customerId,
                    declarationId,
                });

                const linked = await createDocumentIcs2(
                    {
                        fileId: cmsFile.id,
                        fileReference: cmsFile.link,
                        fileName: cmsFile.filename ?? file.name,
                        fileInfo: {
                            purpose,
                            mimeType: cmsFile.contentType ?? file.type,
                            requestNotificationId: notificationId,
                            referralRequestReference,
                        },
                    },
                    declarationId
                );

                setDocuments((current) => [...current, linked]);
                Notification({
                    type: 'success',
                    messageTitle: 'Attachment added',
                    description: `${file.name} was attached to this referral.`,
                    placement: 'top',
                });
            } catch (error) {
                Notification({
                    type: 'error',
                    messageTitle: 'Upload failed',
                    description: getErrorMessage(error) ?? 'Could not attach the file.',
                    placement: 'top',
                });
            } finally {
                setIsUploading(false);
            }
        },
        [declarationId, customerId, notificationId, referralRequestReference, purpose, selectable.length]
    );

    const remove = useCallback(
        async (document: Ics2UploadedFile) => {
            if (!declarationId) return;
            try {
                await deleteDocument(declarationId, document.id);
                setDocuments((current) => current.filter((candidate) => candidate.id !== document.id));
            } catch (error) {
                Notification({
                    type: 'error',
                    messageTitle: 'Could not remove the attachment',
                    description: getErrorMessage(error) ?? 'Please try again.',
                    placement: 'top',
                });
            }
        },
        [declarationId]
    );

    const getFileUrl = useCallback(async (document: Ics2UploadedFile, action: 'Download' | 'Preview') => {
        const fail = (description: string) =>
            Notification({ type: 'error', messageTitle: `${action} Error`, description, placement: 'top' });
        try {
            const { preSignedUrl } = await downloadFile(document.fileId);
            if (!preSignedUrl) fail('Document is not ready. Please try again shortly.');
            return preSignedUrl || undefined;
        } catch {
            fail(`Could not ${action.toLowerCase()} ${document.fileName}. Please try again shortly.`);
            return undefined;
        }
    }, []);

    const download = useCallback(
        async (document: Ics2UploadedFile) => {
            const url = await getFileUrl(document, 'Download');
            if (!url) return;
            try {
                const response = await fetch(url);
                triggerBrowserDownload(await response.blob(), document.fileName);
            } catch {
                Notification({
                    type: 'error',
                    messageTitle: 'Download Error',
                    description: `Could not download ${document.fileName}. Please try again shortly.`,
                    placement: 'top',
                });
            }
        },
        [getFileUrl]
    );

    const preview = useCallback(
        async (document: Ics2UploadedFile) => {
            const url = await getFileUrl(document, 'Preview');
            if (url) window.open(url, '_blank');
        },
        [getFileUrl]
    );

    const attachmentErrors = useMemo(() => {
        const unsendable = selectable.filter((file) => !isIcs2DocumentSendable(file));
        const pending = unsendable.filter((file) => file.securityChecksStatus === 'PENDING');
        const rejected = unsendable.filter((file) => file.securityChecksStatus !== 'PENDING');

        const errors: string[] = [];

        if (pending.length === 1) errors.push('1 attached file is still being checked for security.');
        if (pending.length > 1) errors.push(`${pending.length} attached files are still being checked for security.`);

        if (rejected.length) {
            const names = rejected.map((file) => file.fileName).join(', ');
            const them = rejected.length === 1 ? 'it' : 'them';
            errors.push(`${names} failed the security check and cannot be sent. Remove ${them} from this answer.`);
        }

        return errors;
    }, [selectable]);

    return {
        documents,
        attachmentErrors,
        isLoading,
        isUploading,
        selectableCount: selectable.length,
        canAddMore: selectable.length < ICS2_MAX_ATTACHMENTS_PER_REFERRAL,
        upload,
        remove,
        download,
        preview,
    };
};

export type Ics2ReferralAttachments = ReturnType<typeof useIcs2ReferralAttachments>;

export default useIcs2ReferralAttachments;
